// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import CommonReasonInputForm from "@modules/service/components/common-reason-input/common-reason-input-form";
import actionCreators from "@modules/service/actionCreator";
import {
  SectionComponent,
  Button,
  ColumnsContainer,
  Loader,
  Row,
  Col,
  View,
  Text,
} from "@components";
import type { Referral } from "@modules/service/reducer/referrals/types";
import type { ApprovableContainer } from "@modules/service/types";
import { ReferralsStatusEnum } from "@modules/service/constants";
import { getReferralSchema, getCurrentReferral } from "@modules/service/selectors";
import {
  CommonReasonInputView,
  CommonOutcomeInputView,
  CommonAlertsView,
  CommonComplianceKpisView,
  RtwFormView,
  ReferralAddEditModal,
} from "@modules/service/components";

type OwnProps = $Exact<{
  disabled: boolean,
  clientId: number,
  data: Referral,
  fullData?: ApprovableContainer<Referral>,
  schema: Object,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreators.SetReferralDraft,
  addReferral: typeof actionCreators.AddReferral,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type OwnState = {
  isModalOpen: boolean,
};

export class ReferralDetailsView extends React.Component<Props, OwnState> {
  state = {
    isModalOpen: false,
    modalStep: 0,
  };

  toggleModal = () => {
    this.setState({ isModalOpen: !this.state.isModalOpen });
  };

  get data(): Referral {
    return this.props.data;
  }
  get schema() {
    return this.props.schema || {};
  }
  get supplierDetailsData() {
    const {
      name,
      description,
      isActive,
      email,
      serviceProvided,
      managerReferrals,
      employeeReferrals,
      manualReferrals,
    } = this.data;

    return [
      {
        columns: [
          {
            data: [
              [this.props.t("SUPPLIER_ID"), (this.props.fullData || {}).id],
              [this.props.t("SUPPLIER_NAME"), name],
              [this.props.t("SUPPLIER_DESCRIPTION"), description],
            ],
          },
          {
            data: [
              [
                this.props.t("STATUS"),
                isActive ? ReferralsStatusEnum.ACTIVE : ReferralsStatusEnum.INACTIVE,
              ],
              [this.props.t("EMAIL_ADDRESS"), email],
              [this.props.t("SERVICE_PROVIDED"), serviceProvided],
            ],
          },
          {
            data: [
              [
                this.props.t("MANAGER_REFERRALS"),
                managerReferrals ? ReferralsStatusEnum.ACTIVE : ReferralsStatusEnum.INACTIVE,
              ],
              [
                this.props.t("EMPLOYEE_REFERRALS"),
                employeeReferrals ? ReferralsStatusEnum.ACTIVE : ReferralsStatusEnum.INACTIVE,
              ],
              [
                this.props.t("MANUAL_REFERRALS"),
                manualReferrals ? ReferralsStatusEnum.ACTIVE : ReferralsStatusEnum.INACTIVE,
              ],
            ],
          },
        ],
      },
    ];
  }

  get supplierTrackingRequiredResponseColumns() {
    return [
      {
        columns: [
          {
            data: [
              [
                this.props.t("REQUIRED_RESPONSE"),
                this.props.t(this.data.supplierTracking.requiredResponse),
              ],
            ],
          },
          {
            data: [
              [
                this.props.t("STATUS"),
                this.props.t(this.data.supplierTracking.isActive ? "ACTIVE" : "INACTIVE"),
              ],
            ],
          },
        ],
      },
    ];
  }

  handleEditStep = (step: number) => () => {
    this.props.setDraft(this.data);
    this.setState({ modalStep: step });
    this.toggleModal();
  };

  render() {
    return (
      <React.Fragment>
        {!this.props.data && <Loader />}
        {/*Supplier Details*/}
        <View className="gs-container">
          <SectionComponent
            title={<h6>{this.props.t("SUPPLIER_DETAILS")}</h6>}
            rightLink={
              <Button materialIcon="edit" onClick={this.handleEditStep(1)}>
                {this.props.t("EDIT")}
              </Button>
            }
          >
            <ColumnsContainer rows={this.supplierDetailsData} />
          </SectionComponent>
        </View>
        {/*Manager Led Referrals*/}
        {this.data.managerReferrals && this.schema.managerLedReferrals && (
          <View className="gs-container">
            <SectionComponent
              title={<h6>{this.props.t("MANAGER_LED_REFERRALS")}</h6>}
              rightLink={
                <Button materialIcon="edit" onClick={this.handleEditStep(2)}>
                  {this.props.t("EDIT")}
                </Button>
              }
            >
              <View gap={6}>
                {/*Alerts for Manager Led Referrals*/}
                <View className="gs-container">
                  <CommonAlertsView
                    title={this.props.t("ALERTS_FOR_MANAGER_LED_REFERRALS")}
                    data={(this.data.managerReferralsSettings || {}).alertConfigurations || {}}
                    schema={this.schema.managerLedReferrals.properties.alertConfigurations.items}
                  />
                </View>
                {/*Declined Referral*/}
                <CommonReasonInputView
                  title={this.props.t("DECLINED_REFERRAL")}
                  data={this.data.managerReferralsSettings.declinedReasons}
                  schema={this.schema.managerLedReferrals.properties.declinedReasons.items}
                />

                {/*Referral Mechanism*/}
                {this.schema.managerLedReferrals && this.data.managerReferralsSettings && (
                  <View className="gs-container" gap={6}>
                    <h6>{this.props.t("REFERRAL_DETAILS_REFERRAL_MECHANISM")}</h6>
                    <RtwFormView
                      schema={this.schema.managerLedReferrals}
                      data={this.data.managerReferralsSettings}
                    />
                  </View>
                )}

                {/*Compliance Settings*/}
                <View className="gs-container">
                  <CommonComplianceKpisView
                    title={this.props.t("COMPLIANCE_SETTINGS")}
                    data={this.data.managerReferralsSettings.kpis}
                    schema={this.schema.managerLedReferrals.properties.kpis.items}
                  />
                </View>
              </View>
            </SectionComponent>
          </View>
        )}
        {/*Employee Led Referrals*/}
        {this.data.employeeReferrals && this.schema.employeeLedReferrals && (
          <View className="gs-container">
            <SectionComponent
              title={<h6>{this.props.t("EMPLOYEE_LED_REFERRALS")}</h6>}
              rightLink={
                <Button materialIcon="edit" onClick={this.handleEditStep(3)}>
                  {this.props.t("EDIT")}
                </Button>
              }
            >
              <View gap={6}>
                {/*Alerts for Employee Led Referrals*/}
                <CommonAlertsView
                  title={this.props.t("REFERRAL_DETAILS_ALERTS_FOR_EMPLOYEE_LED_REFERRALS")}
                  data={(this.data.employeeReferralsSettings || {}).alertConfigurations || {}}
                  schema={this.schema.employeeLedReferrals.properties.alertConfigurations.items}
                />
                {/*Declined Referral*/}
                <View gap="6" flexDirection="column">
                  <h6>{this.props.t("DECLINED_REFERRAL")}</h6>
                  <View>
                    <Text type="label" text={this.props.t("NEXT_STEPS_INSTRUCTIONS")} />
                    <textarea
                      disabled={true}
                      className="message-box"
                      rows="4"
                      value={this.data.employeeReferralsSettings.nextStepsInstruction || ""}
                    />
                  </View>
                  <View>
                    <View>
                      <Text type="h6">{this.props.t("REASONS_FOR_DECLINING")}</Text>
                    </View>
                    <CommonReasonInputForm
                      data={this.data.employeeReferralsSettings.declinedReasons}
                      schema={this.schema.employeeLedReferrals.properties.declinedReasons.items}
                      disabled={true}
                    />
                  </View>
                </View>

                {/*Referral Questions*/}
                {this.schema.managerLedReferrals && this.data.employeeReferralsSettings && (
                  <View>
                    <h6>{this.props.t("REFERRAL_QUESTIONS")}</h6>
                    <RtwFormView
                      schema={this.schema.employeeLedReferrals}
                      data={this.data.employeeReferralsSettings}
                    />
                  </View>
                )}
              </View>
            </SectionComponent>
          </View>
        )}
        {this.data.supplierTracking && this.schema.supplierTracking && (
          <View className="gs-container">
            <SectionComponent
              title={<h6>{this.props.t("SUPPLIER_TRACKING")}</h6>}
              rightLink={
                <Button materialIcon="edit" onClick={this.handleEditStep(4)}>
                  {this.props.t("EDIT")}
                </Button>
              }
            >
              <View gap={6}>
                <ColumnsContainer rows={this.supplierTrackingRequiredResponseColumns} />
                {/* Declined Referral*/}
                {this.data.supplierTracking.requiredResponse === "ACCEPT_REFUSE" &&
                  (this.schema || {}).supplierTracking && (
                    <CommonReasonInputView
                      title={this.props.t("DECLINED_REFERRAL")}
                      data={this.data.supplierTracking.declinedReasons}
                      schema={this.schema.supplierTracking.properties.declinedReasons.items}
                    />
                  )}
                {/*Outcome options*/}
                <View className="gs-container">
                  <CommonOutcomeInputView
                    title={this.props.t("TREATMENT_OPTIONS")}
                    data={this.data.supplierTracking.outcomes}
                    schema={this.schema.supplierTracking.properties.outcomes.items}
                  />
                </View>
                <View className="gs-container">
                  {/*Compliance Settings*/}
                  <CommonComplianceKpisView
                    title={this.props.t("COMPLIANCE_SETTINGS")}
                    data={this.data.supplierTracking.kpis}
                    schema={this.schema.supplierTracking.properties.kpis.items}
                  />
                </View>
              </View>
            </SectionComponent>
          </View>
        )}
        {this.data.manualReferrals && (
          <View className="gs-container">
            <SectionComponent
              title={<h6>{this.props.t("MANUAL_REFERRALS")}</h6>}
              rightLink={
                <Button materialIcon="edit" onClick={this.handleEditStep(5)}>
                  {this.props.t("EDIT")}
                </Button>
              }
            >
              <div />
              <CommonReasonInputView
                title={this.props.t("REFERRAL_DETAILS_REASONS_FOR_REFERRAL")}
                data={((this.data || {}).manualReferralsSettings || {}).reasons}
                schema={this.schema.manualReferralsSettings}
              />
            </SectionComponent>
          </View>
        )}
        {this.state.isModalOpen && (
          <ReferralAddEditModal
            schema={this.props.schema}
            onClose={this.toggleModal}
            clientId={this.props.clientId}
            container={this.props.fullData}
            defaultStep={this.state.modalStep}
            editing
          />
        )}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreators.SetReferralDraft,
  addReferral: actionCreators.AddReferral,
};

export default compose(localize(), connect(null, mapDispatchToProps))(ReferralDetailsView);
